import type {
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  RefAttributes,
} from 'react';
import { forwardRef } from 'react';
import { css, cx } from '@eti/styles';
import { vars } from '@etg/wings';
import { resetButtonStyles } from '@eti/products/dist/seat-map/utils/styles';
import { focusStyles } from '../../utils/styleUtils';

const listStyles = css`
  background-color: #fff;
  border: 1px solid ${vars.colors.divider};
  border-radius: 3px;
  box-shadow: 1px 1px 2px 0 rgb(0 0 0 / 10%);
  position: absolute;
`;

const listItemStyles = css`
  width: 100%;

  a {
    text-decoration: none;
  }

  button {
    ${resetButtonStyles};
  }

  button,
  a {
    color: ${vars.colors.text.primary};
    cursor: pointer;
    padding: 16px;
    width: 100%;
    ${focusStyles}

    &:focus-visible {
      outline-offset: -0.1875rem;
    }

    &:hover {
      background-color: ${vars.colors.inputs.hover};
      transition: 0.3s;
    }
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${vars.colors.divider};
  }
`;

interface DropdownMenuItemProps {
  children:
    | ReactElement<HTMLAttributes<HTMLAnchorElement>>
    | ReactElement<HTMLAttributes<HTMLButtonElement>>;
  className?: string;
}

const DropdownMenuItem = ({ children, className }: DropdownMenuItemProps) => {
  return (
    <div className={cx(className, listItemStyles)} role="listitem">
      {children}
    </div>
  );
};

interface DropdownMenuProps {
  children: ReactNode;
  className?: string;
  'data-testid': string;
  id?: string;
}

const DropdownMenuComponent = forwardRef<HTMLDivElement, DropdownMenuProps>(
  ({ children, className, 'data-testid': dataTestId, id }, ref) => {
    return (
      <div
        className={cx(className, listStyles)}
        data-testid={dataTestId}
        id={id}
        ref={ref}
        role="list"
      >
        {children}
      </div>
    );
  },
);

interface IDropdownMenu
  extends ForwardRefExoticComponent<DropdownMenuProps & RefAttributes<HTMLDivElement>> {
  Item: typeof DropdownMenuItem;
}

const DropdownMenu = DropdownMenuComponent as IDropdownMenu;
DropdownMenu.Item = DropdownMenuItem;

export default DropdownMenu;
